"use client";

import React from "react";
import {
  Button,
  LoadingSpinner,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@west-tennessee-consulting/ui";
import { Download, Trash2, X } from "lucide-react";

interface GoogleDriveBulkActionBarProps {
  selectedCount: number;
  allowDownload: boolean;
  allowDelete: boolean;
  isDownloading: boolean;
  isDeleting: boolean;
  onDownload: () => void;
  onDelete: () => void;
  onClear: () => void;
}

/**
 * Sticky action bar shown when one or more files are selected in the Drive
 * browser. Delete routes through a confirm dialog and moves items to Trash
 * (recoverable), never a hard delete.
 */
export function GoogleDriveBulkActionBar({
  selectedCount,
  allowDownload,
  allowDelete,
  isDownloading,
  isDeleting,
  onDownload,
  onDelete,
  onClear,
}: GoogleDriveBulkActionBarProps) {
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  if (selectedCount === 0) return null;

  return (
    <div className="sticky top-0 z-raised flex items-center justify-between gap-2 rounded-lg border bg-muted/80 px-3 py-2 backdrop-blur">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" className="p-1" onClick={onClear} aria-label="Clear selection">
          <X className="h-4 w-4" />
        </Button>
        <span className="text-sm font-medium">
          {selectedCount} selected
        </span>
      </div>

      <div className="flex items-center gap-2">
        {allowDownload && (
          <Button variant="outline" size="sm" onClick={onDownload} disabled={isDownloading || isDeleting}>
            {isDownloading ? (
              <LoadingSpinner className="h-4 w-4 mr-2" />
            ) : (
              <Download className="h-4 w-4 mr-2" />
            )}
            Download
          </Button>
        )}

        {allowDelete && (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={() => setConfirmOpen(true)}
            disabled={isDeleting || isDownloading}
          >
            {isDeleting ? (
              <LoadingSpinner className="h-4 w-4 mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Delete
          </Button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move {selectedCount} item{selectedCount === 1 ? "" : "s"} to Trash?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Selected items move to your Google Drive Trash and can be restored from there for 30
              days. Folders you don't have permission to delete are skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmOpen(false);
                onDelete();
              }}
            >
              Move to Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
